import { useRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';
import type { z, ZodRawShape } from 'zod';

import { PostsOrderingEnum } from '@/services/community';

export const useQueriesManage = <T extends ZodRawShape>({
  paramsSchema,
}: {
  paramsSchema: z.ZodObject<T>;
}) => {
  type Params = z.infer<typeof paramsSchema>;
  const getQueryParams = (query: ParsedUrlQuery) => paramsSchema.parse(query);

  const router = useRouter();
  const params = getQueryParams(router.query);

  const setParams = (newParams: Partial<Params>) => {
    if (!router.isReady) return;
    router.replace({
      query: {
        ...router.query,
        ...newParams,
      },
    });
  };

  const deleteParam = (key: string, value?: Params[keyof Params]) => {
    const { [key]: valuesToFiltrate, ...newParams } = router.query;
    // if value is not set delete all key query
    if (!value)
      return router.replace({
        query: newParams,
      });

    // if value is set delete only the value from the array
    if (!valuesToFiltrate) return;
    if (Array.isArray(valuesToFiltrate)) {
      const newFiltredValues = valuesToFiltrate.filter(
        (singleValue) => singleValue !== value
      );
      return router.replace({
        query: {
          [key]: newFiltredValues,
          ...newParams,
        },
      });
    }
    if (valuesToFiltrate === value) {
      return router.replace({
        query: newParams,
      });
    }
  };

  const resetParams = () => {
    if (!router.isReady) return;
    router.replace({ query: undefined });
  };
  return {
    setParams,
    resetParams,
    deleteParam,
    params: {
      ...params,
      ordering: params.ordering ?? PostsOrderingEnum.CREATED_AT,
    },
  };
};
